import { useEffect, useRef, useState } from 'react';

export interface VoiceRecording {
  blob: Blob;
  durationMs: number;
}

// WebM/Opus where the browser has it (Chrome, Edge, Firefox), MP4/AAC on Safari. The server accepts all of them.
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

// About 240 KB a minute, so a minute of speech stays well below the backend's upload cap. Safari may ignore it.
const AUDIO_BITS_PER_SECOND = 32_000;

export const canRecordVoice = () =>
  typeof window !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== 'undefined';

export const pickRecorderMimeType = (): string | undefined =>
  typeof MediaRecorder === 'undefined' ? undefined : MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));

export const formatVoiceTime = (ms: number) => {
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Records one voice message. start() asks for the microphone (it rejects when the user refuses or there is none) and
 * calls onFinished with the recording after stop(), or with null after cancel(). The recording stops by itself after
 * maxSeconds, and the microphone is released the moment it stops.
 */
export const useVoiceRecorder = (maxSeconds: number) => {
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const cancelledRef = useRef(false);
  const startingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const start = async (onFinished: (finished: VoiceRecording | null) => void) => {
    // A second tap while the browser is still asking for the microphone must not start a second recorder.
    if (startingRef.current || recorderRef.current) {
      return;
    }
    startingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // The user left while the browser was asking: do not start recording into nothing.
      if (!mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const mimeType = pickRecorderMimeType();
      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, {
          ...(mimeType ? { mimeType } : {}),
          audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
        });
      } catch (err) {
        // The microphone is already open: free it, or the browser keeps showing the recording indicator.
        stream.getTracks().forEach((track) => track.stop());
        throw err;
      }

      const chunks: Blob[] = [];
      const startedAt = Date.now();
      cancelledRef.current = false;

      const timer = window.setInterval(() => {
        const ms = Date.now() - startedAt;
        setElapsedMs(ms);
        if (ms >= maxSeconds * 1000 && recorder.state === 'recording') {
          recorder.stop();
        }
      }, 200);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      recorder.onstop = () => {
        window.clearInterval(timer);
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' });
        const finished = !cancelledRef.current && blob.size > 0;
        recorderRef.current = null;
        if (mountedRef.current) {
          setRecording(false);
          setElapsedMs(0);
        }
        onFinished(finished ? { blob, durationMs: Date.now() - startedAt } : null);
      };

      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } finally {
      startingRef.current = false;
    }
  };

  const stop = () => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  };

  const cancel = () => {
    cancelledRef.current = true;
    stop();
  };

  return { recording, elapsedMs, start, stop, cancel };
};
